import { Box, styled, useTheme } from '@mui/material'
import EidTableGrid from 'components/EidTableGrid/EidTableGrid'
import {
    useAppLocalRightsByRoleId,
    useUnAssignAppRightsFromRoleDefinition,
} from 'hooks'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Filters } from 'components'
import { WarningModal } from 'packages/eid-ui'
import { Icon } from 'packages/eid-icons'
import useSubcomponents from 'useSubcomponents'
import { getFullPathFromUrl, useQuery } from 'packages/core'
import { useHistory } from 'react-router'
import config from 'config'

const TableOverView = styled(Box)({
    '& table': {
        boxShadow: '0 4px 17px 0 rgba(0, 0, 0, 0.07)',
    },
})
const SearchMenuBarFilter = styled(Box)({
    width: '100%',

    '& svg': {
        color: ' #aab0b4',
        marginRight: '3px',
    },
    '& div:nth-child(1)': {
        boxShadow: '0 4px 6px -2px rgba(0, 0, 0, 0.1) !important',
        border: 'solid 1px #ebebed',
        paddingLeft: '0',
        height: '40px',
    },
    '& ::placeholder': {
        color: '#aab0b4',
    },
})
const DeleteButton = styled(Box)({
    paddingRight: '10px',
    '& button': {
        padding: '8px 12px',
        borderRadius: ' 4px',
        border: 'solid 1px #ff4d5f',
        backgroundColor: '#fff',
    },
})
const canAddNewAppRightsControlName =
    'ResourceAdmin-ApplicationDetails-AppRoleDefinition-AddNewAppRights-Control'
const canDeleteAppRightsControlName =
    'ResourceAdmin-ApplicationDetails-AppRoleDefinition-DeleteAppRight-Control'

const RoleDefinitionAppRights = ({ roleId, resourceId }) => {
    const { t } = useTranslation()
    const query = useQuery()
    const history = useHistory()
    const theme = useTheme()
    const [selectedItemToDelete, setSelectedItemToDelete] = useState(null)

    const endpoint = 'api/LocalRights/byRoleDefinitionId'

    const { list, loading, loadingMore, pagination } =
        useAppLocalRightsByRoleId(roleId, endpoint)
    const { hasAccessToControl, getAccessBasedAttributes } = useSubcomponents()
    const [deleteAssignments, { isLoading: isDeletingAssignment }] =
        useUnAssignAppRightsFromRoleDefinition(null, roleId)
    const attributes = [
        {
            name: 'friendlyName',
            sortable: true,
            sortBy: 'friendlyName',
            label: 'DisplayName',

            headerCellStyles: {
                fontSize: '13px',
                fontWeight: 'normal',
            },
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-AppRoleDefinition-AppRights-DisplayName',
            },
        },
        {
            name: 'description',
            sortable: true,
            sortBy: 'description',
            label: 'Description',
            headerCellStyles: {
                fontSize: '13px',
                fontWeight: 'normal',
            },
            styles: {
                fontSize: '13px',
                fontWeight: 'normal',
            },
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-AppRoleDefinition-AppRights-Description',
            },
        },
        {
            name: 'rightAllowedAssigeeTypeFriendlyName',
            sortable: true,
            sortBy: 'rightAllowedAssigeeTypeFriendlyName',
            label: 'AllowedMemberTypes',
            headerCellStyles: {
                fontSize: '13px',
                fontWeight: 'normal',
            },
            styles: {
                fontSize: '13px',
                fontWeight: 'normal',
            },
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-AppRoleDefinition-AppRights-AllowedMemberTypes',
            },
        },
        {
            name: 'name',
            sortable: true,
            sortBy: 'name',
            label: 'Value',
            headerCellStyles: {
                fontSize: '13px',
                fontWeight: 'normal',
            },
            styles: {
                fontSize: '13px',
                fontWeight: 'normal',
            },
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-AppRoleDefinition-AppRights-Value',
            },
        },
        {
            name: 'id',
            sortable: true,
            sortBy: 'id',
            label: 'ID',
            headerCellStyles: {
                fontSize: '13px',
                fontWeight: 'normal',
            },
            styles: {
                fontSize: '13px',
                fontWeight: 'normal',
            },
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-AppRoleDefinition-AppRights-ID',
            },
        },

        {
            hideValue: true,
            component: {
                componentName: 'renderer',
                render: (data) => {
                    return (
                        hasAccessToControl(canDeleteAppRightsControlName) && (
                            <DeleteButton>
                                <Button.Delete
                                    showEndIcon={false}
                                    onClick={() =>
                                        setSelectedItemToDelete(data)
                                    }
                                    label={'Remove'}
                                />
                            </DeleteButton>
                        )
                    )
                },
            },
        },
    ]

    const handleDelete = () => {
        deleteAssignments([selectedItemToDelete]).then(() => {
            setSelectedItemToDelete(null)
        })
    }

    return (
        <Box>
            <Box
                width="100%"
                display="flex"
                alignItems="space-between"
                justifyContent="space-between"
                padding="16px 30px"
            >
                {hasAccessToControl(canAddNewAppRightsControlName) && (
                    <Button
                        onClick={() => {
                            query.delete('applicationId')
                            query.delete('roleDefinitionId')
                            query.set('workflowName', 'ManageAzLocalRoleWizard')
                            query.set('returnUrl', getFullPathFromUrl())
                            query.set('SelectedResourceID', resourceId)
                            query.set('WizardAction', 'editazlocalrolerights')

                            history.push(
                                `${
                                    config.APP_SUBPATH
                                }/applications/workflows?${query.toString()}`,
                            )
                        }}
                        style={{
                            borderRadius: '4px',
                            padding: '0px 20px',
                            backgroundColor: theme?.palette?.primary?.main,
                            border: `solid 1px ${theme?.palette?.primary?.main}`,
                            color: '#ffffff',
                            fontSize: '14px',
                            '&:hover': {
                                backgroundColor: theme?.palette?.primary?.main,
                            },
                        }}
                    >
                        <Box style={{ display: 'flex' }}>
                            <Icon
                                name={'Plus'}
                                fill="white"
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    marginRight: '5px',
                                }}
                            />
                            {t('AddAppRight')}
                        </Box>
                    </Button>
                )}

                <Box
                    width="240px"
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                >
                    <SearchMenuBarFilter>
                        <Filters.TextSearch outlined />
                    </SearchMenuBarFilter>
                </Box>
            </Box>
            <TableOverView>
                <EidTableGrid
                    noDataMessage={t('NoDataMessage')}
                    pagination={pagination}
                    isLoading={loading}
                    isLoadingMore={loadingMore}
                    attributes={getAccessBasedAttributes(attributes)}
                    list={list}
                />
            </TableOverView>
            {selectedItemToDelete && (
                <WarningModal
                    title={`${t('Delete')} ${
                        selectedItemToDelete?.friendlyName
                    }`}
                    description={t('AppRightDeleteMessage')}
                    yesLabel={t('Delete')}
                    noLabel={t('Cancel')}
                    open={selectedItemToDelete}
                    cancel={() => setSelectedItemToDelete(null)}
                    proceeding={isDeletingAssignment}
                    proceed={handleDelete}
                />
            )}
        </Box>
    )
}

export default RoleDefinitionAppRights
